import { from, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { axios } from '../axios';
import { EventType } from './meta';

export enum FollowableEventTriggerType {
  Follow = 'Follow',
  Notify = 'Notify',
  None = 'None',
}

export const FollowableEventTriggerTypes = [
  FollowableEventTriggerType.Follow,
  FollowableEventTriggerType.Notify,
  FollowableEventTriggerType.None,
];

export enum DateFieldEventTriggerType {
  AssignedTasks = 'AssignedTasks',
  AllTasks = 'AllTasks',
}

export const DateFieldEventTriggerTypes = [
  DateFieldEventTriggerType.AssignedTasks,
  DateFieldEventTriggerType.AllTasks,
];

export enum Channel {
  Email = 'EMAIL',
  InApp = 'IN_APP',
  Slack = 'SLACK',
}

export interface ChannelConfiguration {
  [Channel.Email]: boolean;
  [Channel.InApp]: boolean;
  [Channel.Slack]: boolean;
}

interface BaseConfiguration {
  channels: ChannelConfiguration;
}

export interface SimpleConfiguration extends BaseConfiguration {
  eventType: Exclude<
    EventType,
    | EventType.TaskCreated
    | EventType.DocumentCreated
    | EventType.TaskStartDateChanged
    | EventType.TaskDueDateChanged
    | EventType.TaskDueDateOverdue
    | EventType.TaskStartDateReminder
    | EventType.TaskDueDateReminder
  >;
}

export interface FollowableConfiguration extends BaseConfiguration {
  eventType: EventType.TaskCreated | EventType.DocumentCreated;
  eventTriggerType: FollowableEventTriggerType;
}

export interface DateFieldConfiguration extends BaseConfiguration {
  eventType:
    | EventType.TaskStartDateChanged
    | EventType.TaskDueDateChanged
    | EventType.TaskDueDateOverdue;
  eventTriggerType: DateFieldEventTriggerType;
}

export enum ReminderOption {
  SameDay = 'SameDay',
  OneDayBefore = 'OneDayBefore',
  TwoDaysBefore = 'TwoDaysBefore',
  ThreeDaysBefore = 'ThreeDaysBefore',
  OneWeekBefore = 'OneWeekBefore',
}

export interface ReminderConfiguration extends BaseConfiguration {
  eventType: EventType.TaskStartDateReminder | EventType.TaskDueDateReminder;
  eventTriggerType: DateFieldEventTriggerType;
  meta: {
    type: ReminderOption;
  };
}

export type Configuration =
  | SimpleConfiguration
  | FollowableConfiguration
  | DateFieldConfiguration
  | ReminderConfiguration;

export const isReminder = (
  configuration: Configuration,
): configuration is ReminderConfiguration =>
  [EventType.TaskStartDateReminder, EventType.TaskDueDateReminder].includes(
    configuration.eventType,
  );

export const isDateField = (
  configuration: Configuration,
): configuration is DateFieldConfiguration =>
  [
    EventType.TaskStartDateChanged,
    EventType.TaskDueDateChanged,
    EventType.TaskDueDateOverdue,
  ].includes(configuration.eventType);

export const isFollowable = (
  configuration: Configuration,
): configuration is FollowableConfiguration =>
  [EventType.TaskCreated].includes(configuration.eventType);

export const isSimple = (
  configuration: Configuration,
): configuration is SimpleConfiguration =>
  !isReminder(configuration) &&
  !isDateField(configuration) &&
  !isFollowable(configuration);

export const configuration = {
  get: (): Observable<Configuration[]> =>
    axios().pipe(
      take(1),
      switchMap(({ get }) =>
        from(get<Configuration[]>('/system-rules/config')),
      ),
      map((response) => response.data),
    ),
  update: (updated: Configuration): Observable<void> =>
    axios().pipe(
      take(1),
      switchMap(({ post }) =>
        from(post<Configuration, void>('/system-rules/config', updated)),
      ),
    ),
};

export const customerConfiguration = {
  get: (): Observable<Configuration[]> =>
    axios().pipe(
      take(1),
      switchMap(({ get }) =>
        from(get<Configuration[]>('/customer-notification-config')),
      ),
      map((response) => response.data),
    ),
  update: (updated: Configuration): Observable<void> =>
    axios().pipe(
      take(1),
      switchMap(({ post }) =>
        from(
          post<Configuration, void>('/customer-notification-config', updated),
        ),
      ),
    ),
};
